import { defaultPaginationQuery, QueryOptions } from 'sync/query';
import { deleteRequest, getRequest, postRequest, putRequest } from 'sync/request';
import {
  AppCollectionDocContext,
  AppEndpointContext,
  AppServiceAdminGetUserContext,
  AppServiceAdminListEndpointUsersContext,
  AppServiceAdminListUsersContext,
  AppServiceAdminUpdateUserContext,
  AppServiceContext,
  AppServiceEndpointContext,
  AppServiceEndpointUserCollectionsContext,
  AppServiceEndpointUserContext,
  OrganizationContext,
  ProjectContext,
  SortParams,
} from 'sync/request.types';
import {
  AdminUserListResponse,
  AdminUserResponse,
  AppCollectionDoc,
  AppCollectionDocResponse,
  AppEndpointConfigPayload,
  AppEndpointConnectResponse,
  AppEndpointListResponse,
  AppEndpointOidcPayload,
  AppEndpointResponse,
  AppEndpointSyncResponse,
  AppServiceCreateResponse,
  AppServiceDeploymentCostPayload,
  AppServiceDeploymentCostResponse,
  AppServiceDeploymentOptions,
  AppServiceEndpointCertResponse,
  AppServiceListResponse,
  AppServiceResponse,
  AppServiceSpecResponse,
  AppUserCollectionsResponse,
  AppUserData,
  AppUserDataListResponse,
  AppUserDataResponse,
  CollectionAppEndpointMapResponse,
  CreateAdminUserPayload,
  CreateAdminUserResponse,
  CreateAppEndpointPayload,
  CreateAppEndpointResponse,
  CreateAppServicePayload,
  CreateAppUserPayloadCollections,
  ListAccessControlValidationListResponse,
  ResumeCollectionAppEndpointPayload,
  ResyncRequest,
  UpdateAppEndpointPayload,
  UpdateAppEndpointResponse,
  UpdateAppServicePayload,
  UpdateAppUserCollectionPayload,
} from './app-service-service.types';

/**
 * Request to return a list of `AppServices`.
 */
export function listAppServices({ organizationId }: OrganizationContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<AppServiceListResponse>(`/v2/organizations/${organizationId}/backends`, { params });
}

/**
 * Request to return a list of `AppServices` for a given project
 */
export function listProjectAppServices({ organizationId, projectId }: ProjectContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<AppServiceListResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/backends`, { params });
}

/**
 * Request to return a specified `AppService`.
 */
export function getAppService({ appServiceId, projectId, organizationId, databaseId }: AppServiceContext) {
  return getRequest<AppServiceResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}`
  );
}

/**
 * Request to return a specified `AppService`.
 */
export function createAppService({ organizationId }: OrganizationContext, data: CreateAppServicePayload) {
  return postRequest<AppServiceCreateResponse>(`/v2/organizations/${organizationId}/backends`, { data });
}

/**
 * Request to update the configuration for a specified `AppService`.
 */
export function updateAppService(
  { appServiceId, projectId, organizationId, databaseId }: AppServiceContext,
  data: UpdateAppServicePayload
) {
  return postRequest<AppServiceResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}`,
    {
      data,
    }
  );
}

/**
 * Request to delete a specified `AppService`.
 */
export function deleteAppService({ appServiceId, projectId, organizationId, databaseId }: AppServiceContext) {
  return deleteRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}`);
}

/**
 * Request to return the specifications of a given database associated with a given `AppServices`.
 */
export function getAvailableSpecsAppServices({ appServiceId, projectId, organizationId, databaseId }: AppServiceContext) {
  return getRequest<AppServiceSpecResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/specs`
  );
}

/**
 * Request to return all databases associated with a given `AppServices`.
 */
export function listEndpointsAppServices(
  { appServiceId, projectId, organizationId, databaseId }: AppServiceContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return getRequest<AppEndpointListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases`,
    { params }
  );
}

/**
 * Request to return access control and data validation list for a given `AppEndpoint`.
 */
export function listAccessControlValidationList({
  appServiceId,
  projectId,
  organizationId,
  databaseId,
  appEndpointId,
}: AppServiceEndpointContext) {
  return getRequest<ListAccessControlValidationListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}`
  );
}

/**
 * Request to return get sync doc for a collection.
 */
export function getEndpointAccessControlDoc({
  projectId,
  appEndpointId,
  organizationId,
  databaseId,
  appServiceId,
  scopeKey,
  collectionKey,
}: AppCollectionDocContext) {
  return getRequest<AppCollectionDocResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}.${scopeKey}.${collectionKey}/sync`
  );
}

/**
 * Request to update sync doc for a collection.
 */
export function updateEndpointAccessControlDoc(
  { projectId, appEndpointId, organizationId, databaseId, appServiceId, scopeKey, collectionKey }: AppCollectionDocContext,
  data: string
) {
  return putRequest<AppCollectionDoc>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}.${scopeKey}.${collectionKey}/sync`,
    { data }
  );
}

/**
 * Request to retrieve a particular endpoint.
 *
 * **Important** this is a temporary solution for launch as no getEndpointById API exists for us to use.
 * Request to return all collections associated with a given `App Endpoint`.
 */
export function getCollectionsEndpointMap(params: AppServiceContext & { bucketName: string; scopeName: string }) {
  return getRequest<CollectionAppEndpointMapResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/buckets/${params.bucketName}/scopes/${params.scopeName}/collections-endpoints`
  );
}

// /**
//  * Request to return app Endpoint`.
//  */
export function getAppEndpoint(params: AppServiceEndpointContext) {
  return getRequest<AppEndpointResponse>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/databases/${params.appEndpointId}`
  );
}

// /**
//  * Request to update single coll import filter`.
//  */

export function updateCollImportFilter(params: AppEndpointContext & { scopeId: string; collectionId: string }, data: string) {
  return putRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/databases/${params.appEndpointId}.${params.scopeId}.${params.collectionId}/importfilter`,
    { data }
  );
}

/**
 * Request to retrieve appEndpoint Access Control and Validation data
 */
export function getEndpointAccessControlValidation({
  projectId,
  appEndpointId,
  organizationId,
  databaseId,
  appServiceId,
}: AppEndpointContext) {
  return listAccessControlValidationList({ projectId, appEndpointId, organizationId, databaseId, appServiceId }).then((response) => {
    return response;
  });
}

/**
 * Request to update app endpoint config`.
 */
export function updateConfigEndpoint(params: AppServiceEndpointContext, data: AppEndpointConfigPayload) {
  return putRequest(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/databases/${params.appEndpointId}/config`,
    { data }
  );
}

/**
 * Request to return read/write access and data validation policies
 */
export function getAppEndpointSync({ appEndpointId, appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext) {
  return getRequest<AppEndpointSyncResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/sync`
  );
}

/**
 * Request to save read/write access and data validation policies
 */
export function saveAppEndpointSync(
  { appEndpointId, appServiceId, projectId, organizationId, databaseId }: AppServiceEndpointContext,
  data: string
) {
  return putRequest<string>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/sync`,
    { data }
  );
}

/**
 * Request to save read/write access and data validation policies
 */
export function runAppEndpointResync(
  { appEndpointId, appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/resync`,
    { params }
  );
}

/**
 * Request sync status
 */
export function getAppEndpointSyncStatus({
  appEndpointId,
  appServiceId,
  projectId,
  organizationId,
  databaseId,
}: AppServiceEndpointContext) {
  return getRequest<ResyncRequest>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/resync`
  );
}

/**
 * Stop resyncing app service
 */
export function stopResyncAppService({ appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/resync?action=stop`
  );
}

/**
 * Start resyncing app service
 */
export function startResyncCollectionAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  data: ResumeCollectionAppEndpointPayload
) {
  return postRequest<ResyncRequest>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/resync?action=start`,
    { data }
  );
}

/**
 * Request to return a specified database associated with a given `AppServices`.
 */
export function createEndpointAppServices(
  { projectId, appServiceId, organizationId, databaseId }: AppServiceContext,
  data: CreateAppEndpointPayload
) {
  return postRequest<CreateAppEndpointResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases`,
    { data }
  );
}

/**
 * Request to return the available specifications for `AppServices`.
 */
export function getDeploymentOptionsAppServices({ organizationId }: OrganizationContext) {
  return getRequest<AppServiceDeploymentOptions>(`/v2/organizations/${organizationId}/backends/deployment-options`);
}

/**
 * Request to return the deployment costs for `AppServices`.
 */
export function getDeploymentCostsAppServices({ organizationId }: OrganizationContext, data: AppServiceDeploymentCostPayload) {
  return postRequest<AppServiceDeploymentCostResponse>(`/v2/organizations/${organizationId}/backends/deployment-costs`, { data });
}

/**
 * Request to return the deployment costs for `AppServices Endpoint`.
 */
export function getPublicCertificateAppServices({
  appServiceId,
  appEndpointId,
  organizationId,
  databaseId,
  projectId,
}: AppEndpointContext) {
  return getRequest<AppServiceEndpointCertResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/publiccert`
  );
}

/**
 * Connect `AppService Endpoint`.
 */
export function connectEndpointAppService({
  appServiceId,
  projectId,
  appEndpointId,
  organizationId,
  databaseId,
}: AppServiceEndpointContext) {
  return getRequest<AppEndpointConnectResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/connect`
  );
}

/**
 * Pause `AppService Endpoint`.
 */
export function pauseEndpointAppService({ appEndpointId, appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/offline`
  );
}

/**
 * Resume `AppService Endpoint`.
 */
export function resumeEndpointAppService({
  appEndpointId,
  appServiceId,
  projectId,
  organizationId,
  databaseId,
}: AppServiceEndpointContext) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/online`
  );
}

/**
 * Updates and `AppService Endpoint`.
 */
export function updateEndpointAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  data: UpdateAppEndpointPayload
) {
  return putRequest<UpdateAppEndpointResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}`,
    { data }
  );
}

/**
 * Delete `AppService Endpoint`.
 */
export function deleteEndpointAppService({
  appEndpointId,
  appServiceId,
  organizationId,
  databaseId,
  projectId,
}: AppServiceEndpointContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}`
  );
}

/**
 * Toggle `App Endpoint basicauth`.
 */
export function toggleBasicAuthAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/basicauth`,
    { params }
  );
}

/**
 * Toggle `App Endpoint anonymous`.
 */
export function toggleAnonymousAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/anonymous`,
    { params }
  );
}

/**
 * Save `App Endpoint oidc settigns`.
 */
export function saveOidcAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  data: AppEndpointOidcPayload
) {
  return postRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/oidc`,
    { data }
  );
}

/**
 * Disable `App Endpoint oidc`.
 */
export function deleteOidcAppService({ appServiceId, appEndpointId, organizationId, databaseId, projectId }: AppServiceEndpointContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/oidc`
  );
}

/**
 * Delete the import filter of an endpoint.
 */
export function deleteEndpointImportFilterAppService({
  appServiceId,
  appEndpointId,
  organizationId,
  databaseId,
  projectId,
}: AppServiceEndpointContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/importfilter`
  );
}

/**
 * Return a list of admin credentials.
 */
export function listAdminUsersAppService(
  { appEndpointId, appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return getRequest<AdminUserListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/adminusers`,
    { params }
  );
}

/**
 * Create admin credentials.
 */
export function createAdminUserAppService(
  { appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  data: CreateAdminUserPayload
) {
  return postRequest<CreateAdminUserResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/adminusers`,
    { data }
  );
}

/**
 * Delete admin credentials
 */
export function deleteAdminUserAppService({
  appServiceId,
  appEndpointId,
  organizationId,
  databaseId,
  projectId,
  name,
}: AppServiceEndpointUserContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/adminusers/${name}`
  );
}

/**
 * Search for a user by name
 */
export function getAdminUserAppService({
  appServiceId,
  appEndpointId,
  organizationId,
  databaseId,
  name,
  projectId,
}: AppServiceEndpointUserContext) {
  return getRequest<AdminUserResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/adminusers/${name}`
  );
}

/**
 * Return a list of users (non-admins)
 */
export function listUsersAppService(
  { appEndpointId, appServiceId, organizationId, projectId, databaseId }: AppServiceEndpointContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return getRequest<AppUserDataListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users`,
    { params }
  );
}

/**
 * Search for a user by name
 */
export function getUserAppService({
  appServiceId,
  appEndpointId,
  organizationId,
  databaseId,
  projectId,
  name,
}: AppServiceEndpointUserContext) {
  return getRequest<AppUserDataResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users/${encodeURIComponent(
      name
    )}`
  );
}

/**
 * Create user.
 */
export function createUserAppService(
  { appEndpointId, appServiceId, organizationId, databaseId, projectId }: AppServiceEndpointContext,
  data: CreateAppUserPayloadCollections
) {
  return postRequest<AppUserData>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users`,
    { data }
  );
}

/**
 * Update user
 */
export function updateUserAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId, name }: AppServiceEndpointUserContext,
  data: UpdateAppUserCollectionPayload
) {
  return putRequest<AppUserData>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users/${encodeURIComponent(
      name
    )}`,
    { data }
  );
}

/**
 * Update user password
 */
export function updateUserPasswordAppService(
  { appEndpointId, appServiceId, organizationId, databaseId, projectId, name }: AppServiceEndpointUserContext,
  data: UpdateAppUserCollectionPayload
) {
  return putRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users/${encodeURIComponent(
      name
    )}/password`,
    { data }
  );
}

/**
 * Delete user
 */
export function deleteUser({ organizationId, projectId, databaseId, appServiceId, appEndpointId, name }: AppServiceEndpointUserContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users/${encodeURIComponent(
      name
    )}`
  );
}

/**
 *Turn off App Service
 */

export const turnAppServicesOff = (params: AppServiceContext) => {
  return postRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/off`
  );
};

/**
 *Turn on App Service
 */

export const turnAppServicesOn = (params: AppServiceContext) => {
  return postRequest<{}>(
    `/v2/organizations/${params.organizationId}/projects/${params.projectId}/clusters/${params.databaseId}/backends/${params.appServiceId}/on`
  );
};

/**
 * List all admin users
 */
export function listAppAdminUsers(
  { appServiceId, organizationId, databaseId, projectId }: AppServiceAdminListUsersContext,
  paginationDetails: QueryOptions = defaultPaginationQuery,
  sortDetails: SortParams = { sortBy: 'name', sortDirection: 'asc' }
) {
  return getRequest<AdminUserListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/adminusers`,
    {
      params: {
        ...paginationDetails,
        ...sortDetails,
      },
    }
  );
}

export function listEndpointAdminUsers(
  { appServiceId, organizationId, databaseId, projectId, appEndpointId }: AppServiceAdminListEndpointUsersContext,
  paginationDetails: QueryOptions = defaultPaginationQuery,
  sortDetails: SortParams = { sortBy: 'name', sortDirection: 'asc' }
) {
  return getRequest<AdminUserListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/database-adminusers/${appEndpointId}`,
    {
      params: {
        ...paginationDetails,
        ...sortDetails,
      },
    }
  );
}

/**
 * Get a single admin user
 */

export function getAppAdminUser({ appServiceId, organizationId, databaseId, projectId, userId }: AppServiceAdminGetUserContext) {
  return getRequest<AdminUserResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/adminusers/${userId}`
  );
}

/**
 * Delete a single admin user
 */

export function deleteAdminUser({ appServiceId, organizationId, databaseId, projectId, userId }: AppServiceAdminGetUserContext) {
  return deleteRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/adminusers/${userId}`
  );
}

/**
 * Update a single admin user
 */
export function updateAdminData({
  appServiceId,
  organizationId,
  databaseId,
  projectId,
  userId,
  userData,
}: AppServiceAdminUpdateUserContext) {
  return putRequest<{}>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/adminusers/${userId}`,
    { data: userData }
  );
}

/**
 * getUserAppCollections
 */
export function getUserCollectionsAppService(
  { appServiceId, appEndpointId, organizationId, databaseId, projectId, name, scopeName }: AppServiceEndpointUserCollectionsContext,
  params: QueryOptions = defaultPaginationQuery
) {
  return getRequest<AppUserCollectionsResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/clusters/${databaseId}/backends/${appServiceId}/databases/${appEndpointId}/users/${encodeURIComponent(
      name
    )}/scopes/${scopeName}/collections`,
    { params }
  );
}
